import { useEffect, useReducer } from 'react';
import Cookies from 'universal-cookie';

export interface CartProduct {
  id: number;
  name: string;
  price: number;
  premium_price: number;
  stripe_plan: string;
  premium_id: string;
  description?: string;
}

export interface CartJobType {
  id: number;
  premium: boolean;
  name?: string;
  title?: string;
  description?: string;
}

export interface CartItem {
  id: number;
  product: CartProduct;
  jobType: CartJobType;
  quantity: number;
}

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: CartProduct; jobType: CartJobType } }
  | { type: 'REMOVE_ITEM'; payload: CartItem }
  | { type: 'UPDATE_QUANTITY'; payload: { id: number; quantity: number } }
  | { type: 'UPDATE_JOB_TYPE'; payload: { id: number; jobType: CartJobType } }
  | { type: 'UPDATE_PRODUCT'; payload: { id: number; product: CartProduct } };

const CART_COOKIE_KEY = 'recruiter_service_cart';
const cookies = new Cookies();
const initialState: CartState = { items: [] };

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM':
      return {
        ...state,
        items: [
          ...state.items,
          {
            id: state.items.length + 1,
            product: action.payload.product,
            jobType: action.payload.jobType,
            quantity: 1,
          },
        ],
      };
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload.id),
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? { ...item, quantity: action.payload.quantity } : item
        ),
      };
    case 'UPDATE_JOB_TYPE':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? { ...item, jobType: action.payload.jobType } : item
        ),
      };
    case 'UPDATE_PRODUCT':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id ? { ...item, product: action.payload.product } : item
        ),
      };
    default:
      return state;
  }
};

const loadCartState = (): CartState => {
  const saved = cookies.get(CART_COOKIE_KEY);

  if (saved && typeof saved === 'object' && Array.isArray(saved.items)) {
    return saved as CartState;
  }

  return initialState;
};

export const useCart = () => {
  const [state, dispatch] = useReducer(cartReducer, initialState, loadCartState);

  useEffect(() => {
    cookies.set(CART_COOKIE_KEY, state, { path: '/' });
  }, [state]);

  const addItem = (product: CartProduct, jobType: CartJobType) =>
    dispatch({ type: 'ADD_ITEM', payload: { product, jobType } });

  const removeItem = (item: CartItem) => dispatch({ type: 'REMOVE_ITEM', payload: item });

  const updateQuantity = (id: number, quantity: number) =>
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });

  const updateJobType = (id: number, jobType: CartJobType) =>
    dispatch({ type: 'UPDATE_JOB_TYPE', payload: { id, jobType } });

  const updateProduct = (id: number, product: CartProduct) =>
    dispatch({ type: 'UPDATE_PRODUCT', payload: { id, product } });

  return {
    state,
    addItem,
    removeItem,
    updateQuantity,
    updateJobType,
    updateProduct,
  };
};
